import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { of as observableOf, Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ErrorResponse, SuccessResponse } from '@shared/services/api/response';
import { IAddGallery, IGallery, IUpdateGallery } from './gallery-model';

@Injectable()
export class GalleryService {
    constructor(private http: HttpClient) {}

    private static get URL(): string {
        return `${environment.dashboardApi.baseUrl}${environment.dashboardApi.prefix}`;
    }
    private static readonly ENDPOINT =
        'events/{eventId}/eventpages/gallery/';

    /** GET event-level gallery files (EventPageFile.EventId), independent of any page. Used to display
     *  the event's existing files when creating a gallery page. Returns { data: { files: [...] } }. */
    public getEventGalleryFiles(
        eventId: string
    ): Observable<SuccessResponse<any> | ErrorResponse> {
        return this.http
            .get<SuccessResponse<any>>(
                GalleryService.URL + `events/${eventId}/galleryfiles`
            )
            .pipe(
                catchError((x) =>
                    observableOf(new ErrorResponse(x?.error?.messages))
                )
            );
    }

    /** GET */
    public getGallery(
        eventId: string,
        id: string
    ): Observable<SuccessResponse<IGallery> | ErrorResponse> {
        return this.http
            .get<SuccessResponse<IGallery>>(
                GalleryService.URL +
                    GalleryService.ENDPOINT.replace('{eventId}', eventId) +
                    id
            )
            .pipe(
                catchError((x) =>
                    observableOf(new ErrorResponse(x?.error?.messages))
                )
            );
    }

    /** POST */
    public addGallery(
        eventId: string,
        galleryAdd: any
    ): Observable<SuccessResponse<boolean> | ErrorResponse> {
        const formData: FormData = new FormData();

        galleryAdd.files.forEach((fileData, index) => {
            formData.append(`files[${index}].index`, JSON.stringify(index));
            formData.append(`files[${index}].displayName`, fileData.displayName);

            if (fileData.translations && fileData.translations.length > 0) {
                fileData.translations.forEach((translation, index2) => {
                    formData.append(`files[${index}].translations[${index2}].languageId`,translation.languageId);
                    formData.append(`files[${index}].translations[${index2}].value`, translation.value);
                });
            }
            if (fileData.file) {
                formData.append(`files[${index}].file`, fileData.file);
            }
            if (fileData.id) {
                formData.append(`files[${index}].id`, fileData.id);
            }
        });

        galleryAdd.contents.forEach((fileData, index) => {
            formData.append(`contents[${index}].languageId`,fileData.languageId);
            formData.append(`contents[${index}].value`, fileData.value);
        });

        galleryAdd.titles.forEach((fileData, index) => {
            formData.append(`titles[${index}].languageId`,fileData.languageId);
            formData.append(`titles[${index}].value`, fileData.value);
        });

        const galleryDisplayType = JSON.stringify(
            galleryAdd.galleryDisplayType
        );
        const galleryAccessType = JSON.stringify(
            galleryAdd.galleryAccessType
        );

        formData.append('galleryDisplayType', galleryDisplayType);
        formData.append('galleryAccessType', galleryAccessType);
        // The create form preloads and submits the event's full file set, so the backend may remove
        // event-level files the user deleted (reconcile like edit). Older cached bundles omit this flag.
        formData.append('manageFiles', 'true');

        return this.http
            .post<SuccessResponse<boolean>>(
                GalleryService.URL +
                    GalleryService.ENDPOINT.replace('{eventId}', eventId),
                formData
            )
            .pipe(
                catchError((x) =>
                    observableOf(new ErrorResponse(x?.error?.messages))
                )
            );
    }

    /** PUT */
    public updateGallery(
        eventId: string,
        id: string,
        galleryUpdate: any
    ): Observable<SuccessResponse<boolean> | ErrorResponse> {

        const formData: FormData = new FormData();

        galleryUpdate.files.forEach((fileData, index) => {
            formData.append(`files[${index}].index`, JSON.stringify(index));
            formData.append(`files[${index}].displayName`, fileData.displayName);

            fileData.translations.forEach((translation, index2) => {
                formData.append(`files[${index}].translations[${index2}].languageId`,translation.languageId);
                formData.append(`files[${index}].translations[${index2}].value`, translation.value);
            });

            if (fileData.file) {
                formData.append(`files[${index}].file`, fileData.file);
            }
            if (fileData.id) {
                formData.append(`files[${index}].id`, fileData.id);
            }
        });

        galleryUpdate.contents.forEach((fileData, index) => {
            formData.append(`contents[${index}].languageId`,fileData.languageId);
            formData.append(`contents[${index}].value`, fileData.value);
        });

        galleryUpdate.titles.forEach((fileData, index) => {
            formData.append(`titles[${index}].languageId`,fileData.languageId);
            formData.append(`titles[${index}].value`, fileData.value);
        });

        const galleryDisplayType = JSON.stringify(
            galleryUpdate.galleryDisplayType
        );
        const galleryAccessType = JSON.stringify(
            galleryUpdate.galleryAccessType
        );

        formData.append('galleryDisplayType', galleryDisplayType);
        formData.append('galleryAccessType', galleryAccessType);

        return this.http
            .put<SuccessResponse<boolean>>(
                GalleryService.URL +
                    GalleryService.ENDPOINT.replace('{eventId}', eventId) +
                    id, formData
            )
            .pipe(
                catchError((x) =>
                    observableOf(new ErrorResponse(x?.error?.messages))
                )
            );
    }

    /** PUT */
    public enableGallery(
        eventId: string,
        id: string
    ): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(
                GalleryService.URL +
                    GalleryService.ENDPOINT.replace('{eventId}', eventId) +
                    id +
                    '/activate',
                null
            )
            .pipe(
                catchError((x) =>
                    observableOf(new ErrorResponse(x?.error?.messages))
                )
            );
    }

    /** PUT */
    public disableGallery(
        eventId: string,
        id: string
    ): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(
                GalleryService.URL +
                    GalleryService.ENDPOINT.replace('{eventId}', eventId) +
                    id +
                    '/deactivate',
                null
            )
            .pipe(
                catchError((x) =>
                    observableOf(new ErrorResponse(x?.error?.messages))
                )
            );
    }

    /** DELETE */
    public deleteGallery(
        eventId: string,
        id: string
    ): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .delete<SuccessResponse<boolean>>(
                GalleryService.URL +
                    GalleryService.ENDPOINT.replace('{eventId}', eventId) +
                    id
            )
            .pipe(
                catchError((x) =>
                    observableOf(new ErrorResponse(x?.error?.messages))
                )
            );
    }
}
